/**
 * TradingStore — demo order manager.
 * In production wire to platformService API + EventDataService.
 * Bridges to Redux so all components stay in sync.
 */
import { store } from '../../redux/store';
import {
  addToast,
} from '../../redux/features/platformSlice';
import MarketDataService from '../market/MarketDataService';
import platformService from './platformService';
// import { getPriceSymbolCandidates } from '../../utils/symbolPriceFilter';

export const ORDER_TYPES = {
  BUY: 0,
  SELL: 1,
  BUY_LIMIT: 2,
  SELL_LIMIT: 3,
  BUY_STOP: 4,
  SELL_STOP: 5,
  BUY_STOP_LIMIT: 6,
  SELL_STOP_LIMIT: 7,
};

const ORDER_TYPE_FROM_API = {
  BUY: ORDER_TYPES.BUY,
  SELL: ORDER_TYPES.SELL,
  BUY_LIMIT: ORDER_TYPES.BUY_LIMIT,
  SELL_LIMIT: ORDER_TYPES.SELL_LIMIT,
  BUY_STOP: ORDER_TYPES.BUY_STOP,
  SELL_STOP: ORDER_TYPES.SELL_STOP,
  BUY_STOP_LIMIT: ORDER_TYPES.BUY_STOP_LIMIT,
  SELL_STOP_LIMIT: ORDER_TYPES.SELL_STOP_LIMIT,
};

export const getOrderTypeCode = (typeOrOrder) => {
  const raw = typeof typeOrOrder === 'object'
    ? (
      typeOrOrder?.type ??
      typeOrOrder?.order_type ??
      typeOrOrder?.action ??
      typeOrOrder?.side
    )
    : typeOrOrder;
  if (typeof raw === 'string' && Number.isNaN(Number(raw))) {
    return ORDER_TYPE_FROM_API[raw.toUpperCase()] ?? ORDER_TYPES.BUY;
  }
  const number = Number(raw);
  return Number.isFinite(number) ? number : ORDER_TYPES.BUY;
};

export const isBuyOrder = (type) => {
  const t = getOrderTypeCode(type);
  return t === 0 || t === 2 || t === 4 || t === 6;
};

export const isSellOrder = (type) => {
  const t = getOrderTypeCode(type);
  return t === 1 || t === 3 || t === 5 || t === 7;
};

export const isPendingOrder = (type) => {
  const t = getOrderTypeCode(type);
  return t >= 2 && t <= 7;
};

export const isMarketOrder = (type) => {
  const t = getOrderTypeCode(type);
  return t === 0 || t === 1;
};

export const getOrderTypeLabel = (type) => {
  const t = getOrderTypeCode(type);
  switch (t) {
    case 0: return 'Buy';
    case 1: return 'Sell';
    case 2: return 'Buy Limit';
    case 3: return 'Sell Limit';
    case 4: return 'Buy Stop';
    case 5: return 'Sell Stop';
    case 6: return 'Buy Stop Limit';
    case 7: return 'Sell Stop Limit';
    default: return 'Order';
  }
};

export const getOrderVolumeLots = (order) => {
  const volume = parseFloat(order?.volume ?? order?.lots ?? order?.volumeInitial ?? order?.volumeCurrent ?? 0);
  return Number.isFinite(volume) ? volume : 0;
};

export const getOrderOpenPrice = (order) => {
  const price = parseFloat(
    order?.priceOpen ??
    order?.openPrice ??
    order?.open_price ??
    order?.priceOrder ??
    order?.price ??
    order?.OpenPrice ??
    order?.result?.price
  );
  return Number.isFinite(price) ? price : 0;
};

export const getOrderCurrentPrice = (order) => {
  const price = parseFloat(
    order?.priceCurrent ??
    order?.closePrice ??
    order?.close_price ??
    order?.currentPrice ??
    order?.current_price ??
    order?.price
  );
  return Number.isFinite(price) ? price : 0;
};

export const getCleanSymbol = (symbol) => {
  const raw = String(symbol || '').trim().toUpperCase();
  return raw.replace(/[^A-Z0-9]/g, '');
};

const getExplicitContractSize = (symbolSettings = {}) => {
  const explicitSize = parseFloat(
    symbolSettings.lot ??
    symbolSettings.contractSize ??
    symbolSettings.contract_size ??
    symbolSettings.contract ??
    symbolSettings.trade_contract_size
  );

  return Number.isFinite(explicitSize) && explicitSize > 0 ? explicitSize : 0;
};

// const getSymbolBaseName = (symbol) => (
//   getPriceSymbolCandidates(symbol).find(candidate => /^[A-Z]{6}$/.test(candidate.toUpperCase())) ||
//   getPriceSymbolCandidates(symbol)[0] ||
//   String(symbol || '')
// ).toUpperCase();

export const getSymbolContractSize = (symbol) => {
  const symbolSettings = MarketDataService.getStaticSymbol(symbol) || {};
  const explicitSize = getExplicitContractSize(symbolSettings);
  const clean = getCleanSymbol(symbol);

  const isForex = /^(EUR|GBP|AUD|NZD|USD|CAD|CHF|JPY|SGD|HKD|CNH|SEK|NOK|ZAR|TRY|MXN|PLN)/i.test(clean) &&
    clean.length >= 6;
  const isGold = clean.includes('XAU') || clean.includes('GOLD');
  const isSilver = clean.includes('XAG') || clean.includes('SILVER');
  const isCrypto = clean.includes('BTC') || clean.includes('ETH') || clean.includes('LTC') || clean.includes('XRP');

  if (isForex) return 100000;
  if (isGold) return 100;
  if (isSilver) return 5000;
  if (isCrypto) return explicitSize > 0 ? explicitSize : 1;

  const marketType = String(symbolSettings.category || symbolSettings.market || symbolSettings.type || '').toLowerCase();
  if (marketType === 'crypto') return 1;
  if (marketType === 'stocks' || marketType === 'stock' || marketType === 'indices' || marketType === 'index') return explicitSize > 0 ? explicitSize : 1;

  return explicitSize > 0 ? explicitSize : 100000;
};

const getOrderContractSize = (order) => {
  const symbol = order?.symbol;
  const clean = getCleanSymbol(symbol);
  const isForex = /^(EUR|GBP|AUD|NZD|USD|CAD|CHF|JPY|SGD|HKD|CNH|SEK|NOK|ZAR|TRY|MXN|PLN)/i.test(clean) &&
    clean.length >= 6;
  const isGold = clean.includes('XAU') || clean.includes('GOLD');
  const isSilver = clean.includes('XAG') || clean.includes('SILVER');

  if (isForex) return 100000;
  if (isGold) return 100;
  if (isSilver) return 5000;

  const orderSize = getExplicitContractSize(order);
  if (orderSize > 0) return orderSize;

  return getSymbolContractSize(symbol);
};

const firstValidId = (...values) => values.find(value => (
  value !== undefined &&
  value !== null &&
  String(value).trim() !== '' &&
  String(value).trim() !== '0'
));

export const getOrderId = (order) => {
  if (isPendingOrder(order)) {
    return firstValidId(
      order?.order,
      order?.ticket,
      order?.id,
      order?._id,
    );
  }

  return firstValidId(
    order?.position,
    order?.position_id,
    order?.positionID,
    order?.positionId,
    order?.ticket,
    order?.order,
    order?.id,
    order?._id,
  );
};

const isSuccessResponse = (res) => {
  if (res === '' || res === null || res === undefined) return true;
  if (typeof res !== 'object') return Boolean(res);
  if (res.status === false) return false;
  return res.success !== false && res.ok !== false && res.error === undefined;
};

const getResponseMessage = (res, fallback) => (
  res?.message || res?.error || res?.msg || fallback
);

const getApiOrderType = (type) => {
  const t = getOrderTypeCode(type);
  switch (t) {
    case ORDER_TYPES.BUY: return 'BUY';
    case ORDER_TYPES.SELL: return 'SELL';
    case ORDER_TYPES.BUY_LIMIT: return 'BUY_LIMIT';
    case ORDER_TYPES.SELL_LIMIT: return 'SELL_LIMIT';
    case ORDER_TYPES.BUY_STOP: return 'BUY_STOP';
    case ORDER_TYPES.SELL_STOP: return 'SELL_STOP';
    case ORDER_TYPES.BUY_STOP_LIMIT: return 'BUY_STOP_LIMIT';
    case ORDER_TYPES.SELL_STOP_LIMIT: return 'SELL_STOP_LIMIT';
    default: return 'BUY';
  }
};

const toNumberOrZero = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const getAccountCurrency = () => {
  const state = store.getState();
  const group = String(state.platform.user?.group || state.platform.account?.group || '');
  const isCent = /cent/i.test(group) || state.platform.account?.is_cent || state.platform.user?.is_cent;
  return (
    state.platform.account?.currency ||
    state.platform.user?.currency ||
    (isCent ? 'USC' : 'USD')
  ).toUpperCase();
};

const getSymbolProfitCurrency = (symbol, symbolSettings = {}) => {
  const settings = symbolSettings || {};
  const explicit = (
    settings.currency_profit ||
    settings.currencyProfit ||
    settings.quote ||
    settings.profitCurrency ||
    ''
  ).toUpperCase();
  if (explicit && explicit !== 'USC' && explicit !== 'CENT') return explicit;

  const clean = getCleanSymbol(symbol);
  if (clean.length >= 6) {
    const quote = clean.slice(3, 6);
    if (['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD'].includes(quote)) {
      return quote;
    }
  }

  return getAccountCurrency();
};

const getConversionPrice = (symbol) => {
  const price = MarketDataService.getPrice(symbol);
  if (!price) return null;
  const bid = Number(price.bid);
  const ask = Number(price.ask);
  if (!Number.isFinite(bid) || !Number.isFinite(ask)) return null;
  return { bid, ask };
};

const convertProfitToAccountCurrency = (profit, profitCurrency) => {
  const accountCurrency = getAccountCurrency();
  const from = String(profitCurrency || accountCurrency).toUpperCase();
  if (
    !Number.isFinite(profit) ||
    from === accountCurrency ||
    (accountCurrency === 'USC' && from === 'USD') ||
    (accountCurrency === 'USD' && from === 'USC')
  ) {
    return profit;
  }

  const direct = getConversionPrice(`${from}${accountCurrency}`) || getConversionPrice(`${from}USD`);
  if (direct) return profit * direct.bid;

  const inverse = getConversionPrice(`${accountCurrency}${from}`) || getConversionPrice(`USD${from}`);
  if (inverse) return profit / inverse.ask;

  return profit;
};

export function calcSingleProfit(order, currentPrice) {
  const symbolSettings = MarketDataService.getStaticSymbol(order.symbol);
  const contractSize = getOrderContractSize(order);

  const volumeLots = getOrderVolumeLots(order);
  const openPrice = getOrderOpenPrice(order);
  const price = parseFloat(currentPrice);
  if (!Number.isFinite(openPrice) || !Number.isFinite(price) || !volumeLots) return 0;

  const rawProfit = isBuyOrder(order)
    ? (price - openPrice) * volumeLots * contractSize
    : (openPrice - price) * volumeLots * contractSize;

  let profit = convertProfitToAccountCurrency(
    rawProfit,
    getSymbolProfitCurrency(order.symbol, symbolSettings)
  );

  // Subtract commission
  const commission = order.commission || order.fee || 0;
  if (commission) {
    profit = profit - Math.abs(commission);
  }

  return isNaN(profit) ? 0 : profit;
}

export function calcClosedProfit(order, openPrice, closePrice) {
  const symbolSettings = MarketDataService.getStaticSymbol(order.symbol);
  const contractSize = getOrderContractSize(order);
  const volumeLots = getOrderVolumeLots(order);
  const open = parseFloat(openPrice);
  const close = parseFloat(closePrice);

  if (!Number.isFinite(open) || !Number.isFinite(close) || !volumeLots) return 0;

  const rawProfit = isBuyOrder(order)
    ? (close - open) * volumeLots * contractSize
    : (open - close) * volumeLots * contractSize;

  let profit = convertProfitToAccountCurrency(
    rawProfit,
    getSymbolProfitCurrency(order.symbol, symbolSettings)
  );

  const costs = Number(order.commission || 0) + Number(order.swap || 0) + Number(order.fee || 0);
  if (costs) profit += costs;

  return Number.isFinite(profit) ? profit : 0;
}

export function calcMargin(order) {
  const symbolSettings = MarketDataService.getStaticSymbol(order.symbol);
  const contractSize = getSymbolContractSize(order.symbol);
  const state = store.getState();
  const leverage = (
    Number(order?.leverage) ||
    Number(state.platform.user?.leverage) ||
    Number(state.platform.account?.leverage) ||
    Number(state.platform.account?.marginLeverage) ||
    Number(symbolSettings?.leverage) ||
    100
  );

  const volumeLots = getOrderVolumeLots(order);
  let margin = (volumeLots * contractSize) / leverage;

  const clean = getCleanSymbol(order.symbol);
  const isGold = clean.includes('XAU') || clean.includes('GOLD');
  const quote = symbolSettings?.quote || (clean.length >= 6 ? clean.slice(3, 6) : 'USD');
  const base = symbolSettings?.base || (clean.length >= 6 ? clean.slice(0, 3) : '');

  if (isGold || quote === 'USD' || quote === 'USC' || clean.endsWith('USD')) {
    const price = MarketDataService.getPrice(order.symbol);
    const rate = Number(price?.bid || price?.ask || getOrderOpenPrice(order) || 1);
    if (isGold || (base && base !== 'USD')) {
      margin = margin * rate;
    }
  } else if (symbolSettings?.exchange) {
    const exchangeRate = MarketDataService.getPrice(symbolSettings.exchange)?.bid || 1;
    margin = margin * exchangeRate;
  }

  return isNaN(margin) ? 0 : parseFloat(margin.toFixed(2));
}



export async function openOrder({ symbol, type, volume, sl, tp, price }) {
  const orderData = {
    symbol: String(symbol || ''),
    order_type: getApiOrderType(type),
    volume: toNumberOrZero(volume),
    price: isPendingOrder(type) ? toNumberOrZero(price) : 0,
    sl: toNumberOrZero(sl),
    tp: toNumberOrZero(tp),
  };

  try {
    const res = await platformService.openOrder(orderData);
    if (!isSuccessResponse(res)) {
      const message = getResponseMessage(res, 'Error');
      notifyOrder(`Failed to open order: ${message}`, 'error');
      return { success: false, message, response: res };
    }
    notifyOrder(`Order opened successfully: ${orderData.symbol} ${getOrderTypeLabel(type)} ${orderData.volume} lots`, 'success');
    return res && typeof res === 'object' ? res : { success: true, data: res };
  } catch (err) {
    const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error';
    notifyOrder(`Failed to open order: ${message}`, 'error');
    return { success: false, message };
  }
}

export async function closePosition(order) {
  const positionId = getOrderId(order);
  try {
    const res = await platformService.closePosition(positionId);
    if (!isSuccessResponse(res)) {
      const message = getResponseMessage(res, 'Error');
      notifyOrder(`Failed to close position: ${message}`, 'error');
      return { success: false, message, response: res };
    }

    notifyOrder(`Position #${positionId} closed successfully`, 'success');
    return res && typeof res === 'object' ? res : { success: true, data: res };
  } catch (err) {
    const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error';
    notifyOrder(`Failed to close position: ${message}`, 'error');
    return { success: false, message };
  }
}

export async function partialClosePosition(order, volume) {
  const positionId = getOrderId(order);
  const closeVolume = toNumberOrZero(volume);

  if (!positionId || closeVolume <= 0) {
    const message = 'Enter a valid partial-close volume';
    notifyOrder(message, 'error');
    return { success: false, message };
  }

  try {
    const res = await platformService.partialClosePosition(positionId, closeVolume);
    if (!isSuccessResponse(res)) {
      const message = getResponseMessage(res, 'Error');
      notifyOrder(`Failed to partially close position: ${message}`, 'error');
      return { success: false, message, response: res };
    }

    notifyOrder(
      `Position #${positionId} partially closed by ${closeVolume} lots`,
      'success',
    );
    return res && typeof res === 'object' ? res : { success: true, data: res };
  } catch (err) {
    const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error';
    notifyOrder(`Failed to partially close position: ${message}`, 'error');
    return { success: false, message };
  }
}

export async function closeOrder(order) {
  try {
    const res = await platformService.closeOrder(getOrderId(order));
    if (!isSuccessResponse(res)) {
      const message = getResponseMessage(res, 'Error');
      notifyOrder(`Failed to close order: ${message}`, 'error');
      return { success: false, message, response: res };
    }

    notifyOrder(`Order #${getOrderId(order)} closed successfully`, 'success');
    return res && typeof res === 'object' ? res : { success: true, data: res };
  } catch (err) {
    const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error';
    notifyOrder(`Failed to close order: ${message}`, 'error');
    return { success: false, message };
  }
}


export async function modifyOrder(order, { sl, tp, price, expire }) {
  const orderData = {
    sl: toNumberOrZero(sl),
    tp: toNumberOrZero(tp)
  };

  try {
    let res;

    if (isPendingOrder(order)) {
      orderData.price = toNumberOrZero(price ?? getOrderOpenPrice(order));
      if (expire) orderData.expire = typeof expire === 'string' ? new Date(expire).getTime() : expire;
      res = await platformService.modifyOrder(getOrderId(order), orderData);
    } else {
      res = await platformService.modifyPosition(getOrderId(order), orderData);
    }

    if (!isSuccessResponse(res)) {
      const message = getResponseMessage(res, 'Error');
      notifyOrder(`Failed to modify order: ${message}`, 'error');
      return { success: false, message, response: res };
    }

    notifyOrder(`Order #${getOrderId(order)} modified successfully`, 'success');
    return res && typeof res === 'object' ? res : { success: true, data: res };
  } catch (err) {
    const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Error';
    notifyOrder(`Failed to modify order: ${message}`, 'error');
    return { success: false, message };
  }
}


export function notifyOrder(message, type = 'info') {
  store.dispatch(addToast({ message, type }));
}





export function calcAccountMetrics() {
  const state = store.getState();
  const orders = (state.platform.positions || []).filter(o => isMarketOrder(o));
  const balance = Number(state.platform.account?.balance || 0);
  const credit = Number(state.platform.account?.credit || 0);
  let totalProfit = 0, totalMargin = 0;
  orders.forEach(o => {
    const p = MarketDataService.getPrice(o.symbol);
    if (p) {
      const cp = isBuyOrder(o) ? p.bid : p.ask;
      totalProfit += calcSingleProfit(o, cp);
    } else {
      totalProfit += Number(o?.profit) || 0;
    }
    totalMargin += calcMargin(o);
  });
  const equity = balance + credit + totalProfit;
  const freeMargin = equity - totalMargin;
  const marginLevel = totalMargin > 0 ? (equity / totalMargin) * 100 : 0;
  return {
    balance, credit: +credit.toFixed(2), equity: +equity.toFixed(2),
    totalProfit: +totalProfit.toFixed(2), totalMargin: +totalMargin.toFixed(2),
    freeMargin: +freeMargin.toFixed(2), marginLevel: +marginLevel.toFixed(1),
  };
}
